import { minutesToMilliseconds } from 'date-fns'
import { Button, Heading, Modal, ScrollView, Text, VStack } from 'native-base'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useInterval from 'react-use/lib/useInterval'
import useUpdate from 'react-use/lib/useUpdate'

import { useConfig } from '../../context/config'

import { Timer } from './Timer'
import { getDuration, getProgress } from './utils'

interface LogoutDialogViewProps {
  onLogout: VoidFunction
  onStaySignedIn: VoidFunction
  progress: number
  duration: Duration
  isLoading: boolean
}

export const LogoutDialogView: React.FC<LogoutDialogViewProps> = ({
  onLogout,
  onStaySignedIn,
  progress,
  duration,
  isLoading,
}) => {
  const { t } = useTranslation()
  const heading = t('logoutDialog.heading')

  return (
    <Modal isOpen onClose={onStaySignedIn}>
      <Modal.Content accessibilityLabel={heading}>
        <ScrollView>
          <Modal.CloseButton
            accessibilityLabel={t('logoutDialog.close.accessibilityLabel')}
            _icon={{ accessibilityHidden: true }}
          />
          <Modal.Body pt={0}>
            <Timer progress={progress} duration={duration} />
            <Heading color={'neutral.900'} textAlign="center" size="md" mt={6}>
              {heading}
            </Heading>
            <Text textAlign="center" mt={2}>
              {t('logoutDialog.text')}
            </Text>
            <VStack space={1} mt={4}>
              <Button isDisabled={isLoading} onPress={onStaySignedIn}>
                {t('logoutDialog.staySignedIn')}
              </Button>
              <Button
                isDisabled={isLoading}
                isLoading={isLoading}
                variant="link"
                _text={{ fontWeight: 700 }}
                onPress={onLogout}
              >
                {t('logoutDialog.logout')}
              </Button>
            </VStack>
          </Modal.Body>
        </ScrollView>
      </Modal.Content>
    </Modal>
  )
}

interface LogoutDialogProps {
  onStaySignedIn: VoidFunction
  onLogout: VoidFunction
  onTimerExpired: VoidFunction
  isLoading: boolean
}

function useStartTime() {
  return useMemo(() => Date.now(), [])
}

function useEndTime(startTime: number) {
  const { config } = useConfig()
  const { warningBeforePatientLogoutInMins } = config
  return useMemo(
    () => startTime + minutesToMilliseconds(warningBeforePatientLogoutInMins),
    [startTime, warningBeforePatientLogoutInMins]
  )
}

export const LogoutDialog: React.FC<LogoutDialogProps> = ({ onStaySignedIn, onLogout, onTimerExpired, isLoading }) => {
  const startTime = useStartTime()
  const endTime = useEndTime(startTime)
  const now = Date.now()
  const duration = getDuration(startTime, endTime, now)
  const progress = getProgress(startTime, endTime, now)
  const update = useUpdate()
  useInterval(update, progress < 100 ? 1000 : null)

  useEffect(() => {
    if (progress === 100) onTimerExpired()
  }, [progress, onTimerExpired])

  return (
    <LogoutDialogView
      isLoading={isLoading}
      onLogout={onLogout}
      onStaySignedIn={onStaySignedIn}
      progress={progress}
      duration={duration}
    />
  )
}
